import { useState } from "react";
import { Icon } from "@iconify/react";
import BusinessBadgeGroup from "./BusinessBadgeGroup";
import { Button } from "../common";
import { Link } from "react-router-dom";
import { useLocalePath } from "../../hooks/useLocalePath";
import { useTranslation } from "../../i18n/I18nContext";
import DirectCallModal from "./DirectCallModal";
import DirectMessageModal from "./DirectMessageModal";
import BookAppointmentModal from "./BookAppointmentModal";
import { resolveBusinessCover, handleImageError } from "../../utils/mediaFallback";
import { PAGE_GUTTER_X } from "../../layout/pageShell";

/** Home discovery row: up to 3 cards. Pass `wrapperClassName` to align with landing gutters. */
const BusinessCardGroupSlider = ({ items, wrapperClassName }) => {
  const lp = useLocalePath();
  const { t } = useTranslation();
  const [isCallOpen, setIsCallOpen] = useState(false);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [isBookAppointmentOpen, setIsBookAppointmentOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const row = (items || []).slice(0, 3);
  if (!row.length) return null;

  return (
    <>
      <div
        className={
          wrapperClassName ??
          `mx-auto w-full max-w-page ${PAGE_GUTTER_X} py-10`
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[30px] lg:gap-[34px] items-stretch">
          {row.map((item) => (
            <div
              key={item.id}
              className="rounded-[15px] hover:shadow-[0_7px_30px_0_rgba(78,_129,_217,_0.17)] transition-all duration-300 bg-white overflow-hidden min-w-0 flex flex-col h-full"
            >
                  <Link to={lp(`/business/profile/${item.slug || item.id}`)}>
                    <div className="w-full h-[224px] relative overflow-hidden">
                      <img
                        src={item.img || resolveBusinessCover(item)}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={handleImageError(item, "cover")}
                      />

                      <div className="absolute flex flex-row items-center gap-3 top-4 left-6">
                        {item.isOpenNow && (
                          <div className="rounded-lg py-[3px] px-2 bg-[rgba(27,_111,_236,_0.80)] flex flex-row items-center gap-1">
                            <Icon
                              icon="mdi:clock-outline"
                              className="w-3 h-3 text-white"
                            />
                            <span className="text-xs text-white">{t("explore.openNow")}</span>
                          </div>
                        )}
                        {item.isVerified && (
                          <div className="rounded-lg py-[3px] px-2 bg-white-blur-09 flex flex-row items-center gap-1">
                            <Icon
                              icon="mdi:check-decagram-outline"
                              className="w-3 h-3 text-primary"
                            />
                            <span className="text-xs text-primary">
                              {t("explore.verified")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>

              <div className="p-5 mt-0.5 flex flex-col flex-1 min-h-0">
                    <div className="mb-5">
                      <div className="w-full flex flex-row items-center justify-between mb-2.5">
                        <h3 className="text-[#101828] lg:text-xl text-lg font-medium line-clamp-1">
                          {item.name}
                        </h3>

                        {item.review && (
                          <div className="flex flex-row items-center gap-1">
                            <img
                              src="/icons/star.svg"
                              alt="star"
                              className="w-3 h-3"
                            />
                            <span className="text-xs font-semibold">
                              {item.review.score}
                            </span>
                            <span className="text-xs">({item.review.count})</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-row items-center gap-2 text-[#253477] mb-5 lg:text-base text-sm">
                        {item.category && <span>{item.category}</span>}
                        {item.category && item.location && <span>•</span>}
                        {item.priceFrom != null && <span>{t("explore.from")} ₪{item.priceFrom}</span>}
                        {item.priceFrom != null && item.location && <span>•</span>}
                        {item.location && (
                          <div className="flex flex-row items-center gap-1 lg:text-base text-sm">
                            <Icon
                              icon="mdi:map-marker-outline"
                              className="w-4 h-4"
                            />
                            <span>{item.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {item.badges && item.badges.length > 0 && (
                      <BusinessBadgeGroup items={item.badges} />
                    )}

                    {item.shortDescription && (
                      <p className="mt-3 text-sm text-[#6A7282] line-clamp-2">{item.shortDescription}</p>
                    )}

                    {item.popularServices && item.popularServices.length > 0 && (
                      <div className="mt-5 bg-[linear-gradient(180deg,#F1F5FF_0%,#FFF_100%)] border-[1.111px] border-primary-blur-01 rounded-[10px] p-[14px]">
                        <h4 className="text-[#253477] text-sm font-semibold mb-2.5">
                          {t("explore.popularServices")}
                        </h4>
                        <div className="flex flex-col gap-2">
                          {item.popularServices.map((ps, index) => (
                            <div
                              key={index}
                              className="flex flex-row items-center justify-between"
                            >
                              <span>{ps.name}</span>
                              <span className="text-primary font-semibold">
                                ₪{ps.price}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                <div
                  className={`w-full mt-auto pt-5 grid gap-2 ${
                    item.ownershipStatus === "claimed" && Number(item.visibleServicesCount ?? 0) > 0
                      ? "grid-cols-3"
                      : "grid-cols-2"
                  }`}
                >
                  <Button
                    variant="secondary-blur"
                    icon="mdi:phone-outline"
                    className="rounded-lg hover:bg-[#1B51EC0D] hover:border-primary"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsCallOpen(true);
                    }}
                  />
                  <Button
                    variant="secondary-blur"
                    icon="mdi:message-outline"
                    className="rounded-lg hover:bg-[#1B51EC0D] hover:border-primary"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsMessageOpen(true);
                    }}
                  />
                  {item.ownershipStatus === "claimed" && Number(item.visibleServicesCount ?? 0) > 0 ? (
                    <Button
                      variant="primary-gradient"
                      label={t("booking.book")}
                      className="rounded-lg"
                      onClick={() => {
                        setSelectedItem(item);
                        setIsBookAppointmentOpen(true);
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <DirectCallModal
        isOpen={isCallOpen}
        onClose={() => setIsCallOpen(false)}
        businessName={selectedItem?.name}
        businessPhoneNumber={selectedItem?.phoneNumber}
        isAvailable={selectedItem?.isAvailable}
      />
      <DirectMessageModal
        isOpen={isMessageOpen}
        onClose={() => setIsMessageOpen(false)}
        businessName={selectedItem?.name}
      />
      {/* <DirectBookAppointmentModal
        isOpen={isBookAppointmentOpen}
        onClose={() => setIsBookAppointmentOpen(false)}
        businessName={selectedItem?.name}
        services={selectedItem?.services}
        times={selectedItem?.businessTimes}
      /> */}
      <BookAppointmentModal
        businessName={selectedItem?.name}
        businessId={selectedItem?.id?.toString()}
        services={selectedItem?.services}
        times={selectedItem?.businessTimes}
        isOpen={isBookAppointmentOpen}
        onClose={() => setIsBookAppointmentOpen(false)}
      />
    </>
  );
};

export default BusinessCardGroupSlider;
